import className from "classnames/bind";
import type { QuizFields, QuizQuestionData } from "./types";
import { ImageAttribution } from "@/components/ImageAttribution";
import { AnimatePresence, motion } from "motion/react";

import styles from "./DigitalExhibitQuiz.module.scss";
const cx = className.bind(styles);

type QuizImageNode = NonNullable<QuizFields["image"]>["node"];

type QuizMediaProps = {
  imageMode?: string | null;
  landingImage?: QuizImageNode | null;
  questions: QuizQuestionData[];
  current: number;
  started?: boolean;
  imageAttribution?: string | null;
  dimImagery?: boolean | null;
  mediaRef?: React.Ref<HTMLDivElement>;
  imgRef?: React.Ref<HTMLImageElement>;
  phase?: "quiz" | "results";
  resultsImage?: QuizImageNode | null;
};

export default function QuizMedia({ imageMode, landingImage, questions, current, started, dimImagery, mediaRef, imgRef, imageAttribution, phase, resultsImage }: QuizMediaProps) {
  const questionImage = questions[current]?.image?.node ?? null;
  // Per-question imagery only kicks in once started, so the landing image shows
  // first (order: landing -> Q1 -> ... -> end), not Q1's image on the landing.
  // Results imagery wins on the results phase; scored quizzes pass none and keep
  // showing the landing or last-question image exactly as before.
  const activeImage =
    phase === "results" && resultsImage
      ? resultsImage
      : imageMode === "multi" && started
        ? questionImage ?? landingImage
        : landingImage;

  return (
    <div ref={mediaRef} className={cx("dx-quiz__media")}>
      <div className={cx("dx-quiz__media-parallax", "js-parallax")} aria-hidden="true">
        {/* Keyed on the URL so a change crossfades (stacked layers) instead of a
            hard src swap. initial={false} skips the first mount's fade-in. */}
        <AnimatePresence initial={false}>
          {activeImage?.mediaItemUrl && (
            <motion.img
              key={activeImage.mediaItemUrl}
              ref={imgRef}
              className={cx("dx-quiz__media-image")}
              src={activeImage.mediaItemUrl}
              srcSet={activeImage.srcSet ?? undefined}
              sizes={activeImage.sizes ?? undefined}
              alt=""
              draggable={false}
              onContextMenu={(e) => e.preventDefault()}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5, ease: "easeInOut" }}
            />
          )}
        </AnimatePresence>
      </div>
      <AnimatePresence>
        {imageAttribution && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            key={imageAttribution}
            className={cx("dx-quiz__media-attribution-wrapper")}
          >
            <ImageAttribution className={cx("dx-quiz__media-attribution")} text={imageAttribution} />
          </motion.div>
        )}
      </AnimatePresence>
      <div className={cx("dx-quiz__media-dim", { "is-dim": Boolean(dimImagery) })} data-quiz-dim />
      <div className={cx("dx-quiz__media-scrim")} />
    </div>
  );
}
